import { useEffect, useState, ChangeEvent, FormEvent, useContext } from 'react';
import { hallPassApiService, HallPassTypeResponse } from '../services/hallPassApiService';
import PathAutocomplete, { PathOption } from '../components/admin/PathAutocomplete';
import { UserContext } from '../contexts/UserContext';
import { Plus, Edit, Trash } from 'lucide-react';
import { useNotification } from '../contexts/NotificationContext';

interface HallPassTypesPageProps {
  searchTerm: string;
  statusFilter: 'all' | 'active' | 'inactive';
}

const HallPassTypesPage = ({ searchTerm, statusFilter }: HallPassTypesPageProps) => {
  const [types, setTypes] = useState<HallPassTypeResponse[]>([]);
  const [editing, setEditing] = useState<Partial<HallPassTypeResponse> | null>(null);
  const [paths, setPaths] = useState<PathOption[]>([]);
  const { userProfile } = useContext(UserContext);
  const { addNotification } = useNotification();

  useEffect(() => {
    fetchTypes();
    loadPaths();
  }, []);

  const fetchTypes = async () => {
    try {
      const data = await hallPassApiService.getHallPassTypes(true);
      setTypes(data);
    } catch (err) {
      console.error('Failed to load hall pass types', err);
    }
  };

  const loadPaths = async () => {
    try {
      if (!userProfile?.orgId) return;
      const { mapId } = await hallPassApiService.getOrCreateDefaultMap(userProfile.orgId);
      const map = await hallPassApiService.getMap(mapId);
      const opts: PathOption[] = (map.paths || []).map(p => {
        const start = map.locations.find(l => l.locationId === p.startLocationId)?.name || p.startLocationId;
        const end = map.locations.find(l => l.locationId === p.endLocationId)?.name || p.endLocationId;
        return { pathId: p.pathId, name: `${start} to ${end}` };
      });
      setPaths(opts);
    } catch (err) {
      console.error('Failed to load paths', err);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!editing) return;
    const { name, value } = e.target;
    setEditing({ ...editing, [name]: value });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;

    try {
      if (editing.hallPassTypeId) {
        await hallPassApiService.updateHallPassType(
          editing.hallPassTypeId,
          editing as Partial<HallPassTypeResponse>
        );
      addNotification('Hall pass type updated', 'success');
      } else {
        const created = await hallPassApiService.createHallPassType(
          editing as Partial<HallPassTypeResponse>
        );
        setTypes([...types, created]);
        addNotification('Hall pass type created', 'success');
      }
      setEditing(null);
      fetchTypes();
    } catch (err) {
      console.error('Failed to save hall pass type', err);
      addNotification('Failed to save hall pass type', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await hallPassApiService.deleteHallPassType(id);
      setTypes(types.filter(t => t.hallPassTypeId !== id));
    } catch (err) {
      console.error('Failed to delete hall pass type', err);
    }
  };

  const filteredTypes = types
    .filter(t => t.hallPassTypeName.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter(t => {
      if (statusFilter === 'active') return !t.deactivatedOn;
      if (statusFilter === 'inactive') return !!t.deactivatedOn;
      return true;
    });

  return (
    <div className="space-y-6 p-4 rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Hall Pass Types</h1>
        <button
          onClick={() => setEditing({ hallPassTypeName: '', hallPassTypeDescription: '', hallPassTypeDuration: 5 })}
          className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          New Type
        </button>
      </div>

      {editing && (
        <form onSubmit={handleSubmit} className="space-y-4 bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <input
                type="text"
                name="hallPassTypeName"
                value={editing.hallPassTypeName || ''}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Duration (min)</label>
              <input
                type="number"
                name="hallPassTypeDuration"
                value={editing.hallPassTypeDuration || 0}
                onChange={handleChange}
                className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <textarea
              name="hallPassTypeDescription"
              value={editing.hallPassTypeDescription || ''}
              onChange={handleChange}
              rows={2}
              className="mt-1 block w-full border border-gray-300 rounded-md p-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Path</label>
            <PathAutocomplete
              paths={paths}
              selectedPathId={editing.pathId || undefined}
              onSelect={pathId => setEditing({ ...editing, pathId })}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-md border">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-indigo-500 text-white rounded-md">Save</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="p-2 text-left">Name</th>
              <th className="p-2 text-left">Duration</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTypes.map(t => (
              <tr key={t.hallPassTypeId} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="p-2">{t.hallPassTypeName}</td>
                <td className="p-2">{t.hallPassTypeDuration}</td>
                <td className="p-2">{t.deactivatedOn ? 'Inactive' : 'Active'}</td>
                <td className="p-2 space-x-2">
                  <button
                    onClick={() => setEditing(t)}
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(t.hallPassTypeId)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HallPassTypesPage;
